<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { toast } from 'vue3-toastify';
import dartsHubApi from '@/axios/dartsHubApi';
import { roleLabel as roleLabelMap } from '@/composables/useRoles';
import { useConfirm } from '@/composables/useConfirm';
import { normalizeForSearch } from '@/functions/utils';

const { open: openConfirm } = useConfirm();

interface Comite { id: number; code: string; name: string; ligue_code: string; }
interface Ligue { id: number; code: string; name: string; }
interface ClubOption { id: number; number: number; name: string; comite: string | null; }

interface AccessRequest {
  id: number;
  email: string;
  name: string;
  firstname: string;
  telephone: string | null;
  requested_role: string;
  club_number: number | null;
  comite_code: string | null;
  ligue_code: string | null;
  new_club_name: string | null;
  new_club_address: string | null;
  message: string | null;
  status: string;
  created_at: string | null;
  processed_at: string | null;
  target_name: string | null;
}

const requests = ref<AccessRequest[]>([]);
const loading = ref(true);
const statusFilter = ref('pending');
const search = ref('');

const ligues = ref<Ligue[]>([]);
const comites = ref<Comite[]>([]);
const clubs = ref<ClubOption[]>([]);

const showClubModal = ref(false);
const clubRequest = ref<AccessRequest | null>(null);
const linkMode = ref<'create' | 'existing'>('create');
const existingClubId = ref<number | null>(null);
const clubSearch = ref('');
const linkingExisting = ref(false);
const clubOptions = computed(() => {
  const q = normalizeForSearch(clubSearch.value);
  if (!q) return clubs.value;
  return clubs.value.filter(c =>
    normalizeForSearch(c.name).includes(q) || String(c.number).includes(q)
  );
});
const clubForm = ref({
  club_number: 0,
  name: '',
  comite_code: '',
  short_code: '',
  club_email: '',
  club_street: '',
  club_complement: '',
  club_postal_code: '',
  club_city: '',
  contact_nom: '',
  contact_street: '',
  contact_complement: '',
  contact_postal_code: '',
  contact_city: '',
  contact_telephone: '',
});
const clubLigueCode = ref('');
const clubComitesOptions = computed(() =>
  clubLigueCode.value ? comites.value.filter(c => c.ligue_code === clubLigueCode.value) : comites.value
);
const approvingClub = ref(false);

const filtered = computed(() => {
  let list = requests.value;
  if (statusFilter.value) list = list.filter(r => r.status === statusFilter.value);
  const q = normalizeForSearch(search.value);
  if (q) list = list.filter(r =>
    normalizeForSearch(r.email).includes(q) ||
    normalizeForSearch(r.name).includes(q) ||
    normalizeForSearch(r.firstname).includes(q)
  );
  return list;
});

const roleLabel = (r: string) => roleLabelMap[r] ?? r;

const targetLabel = (r: AccessRequest) => {
  if (r.requested_role === 'club_representative') {
    if (r.new_club_name) {
      return `"${r.new_club_name}" (n°${r.club_number}) - ${r.new_club_address} - Comité ${r.comite_code}`;
    }
    return `Club n°${r.club_number}${r.target_name ? ` - ${r.target_name}` : ''}`;
  }
  if (r.requested_role === 'comitee_representative') return `Comité ${r.comite_code}${r.target_name ? ` - ${r.target_name}` : ''}`;
  return `Ligue ${r.ligue_code}${r.target_name ? ` - ${r.target_name}` : ''}`;
};

const statusLabel = (s: string) =>
  ({ pending: 'En attente', approved: 'Approuvée', rejected: 'Rejetée' }[s] ?? s);

const statusClass = (s: string) =>
  ({ pending: 'badge-yellow', approved: 'badge-green', rejected: 'badge-red' }[s] ?? 'badge-gray');

const load = async () => {
  loading.value = true;
  try {
    const { data } = await dartsHubApi.get('/access-requests/');
    requests.value = data;
  } catch {
    toast.error('Erreur de chargement.');
  } finally {
    loading.value = false;
  }
};

const approve = async (r: AccessRequest) => {
  if (r.new_club_name) {
    openClubModal(r);
    return;
  }
  if (!(await openConfirm('Approuver la demande', `Approuver l'accès ${roleLabel(r.requested_role)} (${targetLabel(r)}) pour ${r.email} ?`))) return;
  try {
    await dartsHubApi.post(`/access-requests/${r.id}/approve`);
    toast.success('Demande approuvée, accès attribués.');
    await load();
  } catch (err: any) {
    toast.error(err.response?.data?.detail || "Erreur lors de l'approbation");
  }
};

const openClubModal = (r: AccessRequest) => {
  clubRequest.value = r;
  linkMode.value = 'create';
  existingClubId.value = null;
  clubSearch.value = '';
  const contactNom = `${r.firstname} ${r.name}`.trim();
  clubForm.value = {
    club_number: r.club_number ?? 0,
    name: r.new_club_name || '',
    comite_code: r.comite_code || '',
    short_code: '',
    club_email: r.email || '',
    club_street: '',
    club_complement: '',
    club_postal_code: '',
    club_city: '',
    contact_nom: contactNom,
    contact_street: '',
    contact_complement: '',
    contact_postal_code: '',
    contact_city: '',
    contact_telephone: r.telephone || '',
  };
  clubLigueCode.value = comites.value.find(c => c.code === r.comite_code)?.ligue_code || '';
  showClubModal.value = true;
};

const copyClubAddressToContact = () => {
  clubForm.value.contact_street = clubForm.value.club_street;
  clubForm.value.contact_complement = clubForm.value.club_complement;
  clubForm.value.contact_postal_code = clubForm.value.club_postal_code;
  clubForm.value.contact_city = clubForm.value.club_city;
};

const confirmClubApprove = async () => {
  if (!clubRequest.value) return;
  const f = clubForm.value;
  if (!f.club_number || !f.name || !f.comite_code) {
    toast.error('Numéro, nom et comité du club sont requis.');
    return;
  }
  approvingClub.value = true;
  try {
    await dartsHubApi.post(`/access-requests/${clubRequest.value.id}/approve`, {
      club_number: f.club_number,
      name: f.name,
      comite_code: f.comite_code,
      short_code: f.short_code || null,
      club_email: f.club_email || null,
      club_street: f.club_street || null,
      club_complement: f.club_complement || null,
      club_postal_code: f.club_postal_code || null,
      club_city: f.club_city || null,
      contact_nom: f.contact_nom || null,
      contact_street: f.contact_street || null,
      contact_complement: f.contact_complement || null,
      contact_postal_code: f.contact_postal_code || null,
      contact_city: f.contact_city || null,
      contact_telephone: f.contact_telephone || null,
    });
    toast.success('Club créé, affiliation approuvée.');
    showClubModal.value = false;
    await load();
  } catch (err: any) {
    toast.error(err.response?.data?.detail || "Erreur lors de l'approbation");
  } finally {
    approvingClub.value = false;
  }
};

const confirmLinkExisting = async () => {
  if (!clubRequest.value || !existingClubId.value) {
    toast.error('Sélectionnez un club.');
    return;
  }
  linkingExisting.value = true;
  try {
    await dartsHubApi.post(`/access-requests/${clubRequest.value.id}/approve`, {
      existing_club_id: existingClubId.value,
    });
    toast.success('Demande liée au club existant et approuvée.');
    showClubModal.value = false;
    await load();
  } catch (err: any) {
    toast.error(err.response?.data?.detail || "Erreur lors de l'approbation");
  } finally {
    linkingExisting.value = false;
  }
};

const reject = async (r: AccessRequest) => {
  if (!(await openConfirm('Rejeter la demande', `Rejeter la demande de ${r.email} ?`))) return;
  try {
    await dartsHubApi.post(`/access-requests/${r.id}/reject`);
    toast.success('Demande rejetée.');
    await load();
  } catch (err: any) {
    toast.error(err.response?.data?.detail || 'Erreur lors du rejet');
  }
};

onMounted(async () => {
  const [liguesRes, comitesRes, clubsRes] = await Promise.all([
    dartsHubApi.get('/clubs/ligues'),
    dartsHubApi.get('/clubs/comites'),
    dartsHubApi.get('/clubs/', { params: { all_comites: true } }),
  ]);
  ligues.value = liguesRes.data;
  comites.value = comitesRes.data;
  clubs.value = clubsRes.data;
  await load();
});
</script>

<template>
  <div class="page">
    <div class="page-header">
      <h1><i class="fa-solid fa-key" /> Demandes d'accès</h1>
    </div>

    <div class="filters-bar">
      <div class="filter-chips">
        <button class="chip" :class="{ active: statusFilter === '' }" @click="statusFilter = ''">Tous</button>
        <button class="chip chip-yellow" :class="{ active: statusFilter === 'pending' }"
          @click="statusFilter = 'pending'">En attente</button>
        <button class="chip chip-green" :class="{ active: statusFilter === 'approved' }"
          @click="statusFilter = 'approved'">Approuvées</button>
        <button class="chip chip-red" :class="{ active: statusFilter === 'rejected' }"
          @click="statusFilter = 'rejected'">Rejetées</button>
      </div>
      <div class="search-bar">
        <i class="fa-solid fa-magnifying-glass" style="color:var(--text-muted)" />
        <input v-model="search" placeholder="Nom ou email…" />
      </div>
    </div>

    <div v-if="loading" class="loader"><i class="fa-solid fa-spinner fa-spin" /> Chargement…</div>

    <div v-else class="table-wrapper">
      <table>
        <thead>
          <tr>
            <th>Date</th>
            <th>Nom</th>
            <th>Email</th>
            <th>Rôle demandé</th>
            <th>Cible</th>
            <th>Message</th>
            <th>Statut</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="r in filtered" :key="r.id">
            <td style="white-space:nowrap;font-size:0.8rem;color:var(--text-muted)">
              {{ r.created_at ? new Date(r.created_at).toLocaleDateString('fr-FR') : '-' }}
            </td>
            <td style="font-weight:500">{{ r.name }} {{ r.firstname }}</td>
            <td style="font-size:0.85rem">{{ r.email }}</td>
            <td style="font-size:0.85rem">{{ roleLabel(r.requested_role) }}</td>
            <td style="font-size:0.85rem">
              <span v-if="r.new_club_name" class="badge badge-yellow" style="margin-right:4px">Nouveau club</span>
              {{ targetLabel(r) }}
            </td>
            <td style="font-size:0.8rem;color:var(--text-muted);max-width:200px;overflow:hidden;text-overflow:ellipsis">
              {{ r.message || '-' }}
            </td>
            <td><span class="badge" :class="statusClass(r.status)">{{ statusLabel(r.status) }}</span></td>
            <td>
              <div v-if="r.status === 'pending'" style="display:flex;gap:4px">
                <button class="btn-sm btn-green" @click="approve(r)">
                  <i class="fa-solid fa-check" /> Approuver
                </button>
                <button class="btn-sm btn-red" @click="reject(r)">
                  <i class="fa-solid fa-xmark" /> Rejeter
                </button>
              </div>
            </td>
          </tr>
          <tr v-if="!filtered.length">
            <td colspan="8" style="text-align:center;color:var(--text-muted);padding:1.5rem">
              Aucune demande.
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="!loading" class="mobile-cards">
      <div v-for="r in filtered" class="mobile-card" :key="r.id">
        <div class="card-top">
          <span style="font-size:0.8rem;color:var(--text-muted)">
            {{ r.created_at ? new Date(r.created_at).toLocaleDateString('fr-FR') : '-' }}
          </span>
          <span class="badge" :class="statusClass(r.status)">{{ statusLabel(r.status) }}</span>
        </div>
        <div style="font-weight:600;font-size:0.95rem">{{ r.name }} {{ r.firstname }}</div>
        <div style="font-size:0.82rem;color:var(--text-muted)">{{ r.email }}</div>
        <div style="font-size:0.82rem">{{ roleLabel(r.requested_role) }} - {{ targetLabel(r) }}</div>
        <div v-if="r.message" style="font-size:0.8rem;color:var(--text-muted)">{{ r.message }}</div>
        <div v-if="r.status === 'pending'" class="card-actions">
          <button class="btn-sm btn-green" @click="approve(r)">
            <i class="fa-solid fa-check" /> Approuver
          </button>
          <button class="btn-sm btn-red" @click="reject(r)">
            <i class="fa-solid fa-xmark" /> Rejeter
          </button>
        </div>
      </div>
      <div v-if="!filtered.length" style="text-align:center;color:var(--text-muted);padding:1.5rem">Aucune demande.
      </div>
    </div>

    <!-- Modal création club à l'approbation -->
    <div v-if="showClubModal" class="modal-overlay" @click.self="showClubModal = false">
      <div class="modal">
        <div class="modal-header">
          <h2>{{ linkMode === 'existing' ? 'Lier à un club existant' : 'Créer le club et approuver' }}</h2>
          <button class="btn-sm" @click="showClubModal = false"><i class="fa-solid fa-xmark" /></button>
        </div>
        <p style="font-size:0.82rem;color:var(--text-muted);margin-bottom:0.75rem">
          Demande de {{ clubRequest?.name }} {{ clubRequest?.firstname }} ({{ clubRequest?.email }}).
        </p>

        <div class="filter-chips" style="margin-bottom:1rem">
          <button class="chip" :class="{ active: linkMode === 'create' }" @click="linkMode = 'create'">
            Nouveau club
          </button>
          <button class="chip" :class="{ active: linkMode === 'existing' }" @click="linkMode = 'existing'">
            Club déjà existant
          </button>
        </div>

        <template v-if="linkMode === 'existing'">
          <p style="font-size:0.82rem;color:var(--text-muted);margin-bottom:1rem">
            Le club existe déjà en base : sélectionnez-le pour y lier directement le représentant, sans le recréer.
          </p>
          <div class="form-group">
            <label>Rechercher un club</label>
            <input v-model="clubSearch" type="text" placeholder="Nom ou numéro de club…" />
          </div>
          <div class="form-group">
            <label>Club</label>
            <select v-model.number="existingClubId" class="filter-select">
              <option :value="null">-- Sélectionner --</option>
              <option v-for="c in clubOptions" :value="c.id" :key="c.id">
                n°{{ c.number }} - {{ c.name }}{{ c.comite ? ` (${c.comite})` : '' }}
              </option>
            </select>
          </div>
          <div class="form-actions">
            <button class="btn-sm" @click="showClubModal = false">Annuler</button>
            <button class="btn-sm btn-green" :disabled="linkingExisting || !existingClubId" @click="confirmLinkExisting">
              <i v-if="linkingExisting" class="fa-solid fa-spinner fa-spin" /><i v-else class="fa-solid fa-check" />
              Lier au club et approuver
            </button>
          </div>
        </template>

        <template v-else>
        <h3 class="modal-section">Club</h3>
        <div class="form-group">
          <label>Numéro de club</label>
          <input v-model.number="clubForm.club_number" type="number" />
        </div>
        <div class="form-group">
          <label>Nom</label>
          <input v-model="clubForm.name" type="text" placeholder="Nom du club" />
        </div>
        <div class="form-group">
          <label>Code court</label>
          <input v-model="clubForm.short_code" type="text" placeholder="ex: BAD" />
        </div>
        <div class="form-group">
          <label>Ligue</label>
          <select v-model="clubLigueCode" class="filter-select" @change="clubForm.comite_code = ''">
            <option value="">-- Sélectionner --</option>
            <option v-for="l in ligues" :value="l.code" :key="l.id">{{ l.name }} ({{ l.code }})</option>
          </select>
        </div>
        <div class="form-group">
          <label>Comité</label>
          <select v-model="clubForm.comite_code" class="filter-select">
            <option value="">-- Sélectionner --</option>
            <option v-for="c in clubComitesOptions" :value="c.code" :key="c.id">{{ c.name }} ({{ c.code }})</option>
          </select>
        </div>
        <div class="form-group">
          <label>Email du club</label>
          <input v-model="clubForm.club_email" type="email" placeholder="contact@club.fr" />
        </div>
        <div class="form-group">
          <label>Adresse</label>
          <input v-model="clubForm.club_street" type="text" placeholder="Rue" />
        </div>
        <div class="form-group">
          <label>Complément d'adresse</label>
          <input v-model="clubForm.club_complement" type="text" />
        </div>
        <div class="form-group form-group-row">
          <div>
            <label>Code postal</label>
            <input v-model="clubForm.club_postal_code" type="text" />
          </div>
          <div>
            <label>Ville</label>
            <input v-model="clubForm.club_city" type="text" />
          </div>
        </div>

        <h3 class="modal-section">
          Contact affiliation
          <button class="btn-sm" type="button" @click="copyClubAddressToContact">Copier l'adresse du club</button>
        </h3>
        <div class="form-group">
          <label>Nom du contact</label>
          <input v-model="clubForm.contact_nom" type="text" />
        </div>
        <div class="form-group">
          <label>Téléphone</label>
          <input v-model="clubForm.contact_telephone" type="text" />
        </div>
        <div class="form-group">
          <label>Adresse</label>
          <input v-model="clubForm.contact_street" type="text" placeholder="Rue" />
        </div>
        <div class="form-group">
          <label>Complément d'adresse</label>
          <input v-model="clubForm.contact_complement" type="text" />
        </div>
        <div class="form-group form-group-row">
          <div>
            <label>Code postal</label>
            <input v-model="clubForm.contact_postal_code" type="text" />
          </div>
          <div>
            <label>Ville</label>
            <input v-model="clubForm.contact_city" type="text" />
          </div>
        </div>

        <div class="form-actions">
          <button class="btn-sm" @click="showClubModal = false">Annuler</button>
          <button class="btn-sm btn-green" :disabled="approvingClub" @click="confirmClubApprove">
            <i v-if="approvingClub" class="fa-solid fa-spinner fa-spin" /><i v-else class="fa-solid fa-check" />
            Créer le club et approuver
          </button>
        </div>
        </template>
      </div>
    </div>
  </div>
</template>

<style scoped>
.filters-bar {
  display: flex;
  gap: 0.75rem;
  align-items: center;
  margin-bottom: 1rem;
  flex-wrap: wrap;
}

.filter-chips {
  display: flex;
  gap: 0.3rem;
  flex-wrap: wrap;
}

.chip {
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 0.76rem;
  font-weight: 600;
  border: 1px solid var(--border);
  background: var(--bg-input);
  color: var(--text);
  cursor: pointer;
  transition: background 0.12s, border-color 0.12s;
  white-space: nowrap;
}

.chip.active {
  background: var(--text);
  color: var(--bg);
  border-color: var(--text);
}

.chip-yellow.active {
  background: #b45309;
  border-color: #b45309;
  color: #fff;
}

.chip-yellow:not(.active):hover {
  background: rgba(234, 179, 8, 0.12);
  border-color: rgba(234, 179, 8, 0.4);
  color: #b45309;
}

.chip-green.active {
  background: #15803d;
  border-color: #15803d;
  color: #fff;
}

.chip-green:not(.active):hover {
  background: rgba(34, 197, 94, 0.1);
  border-color: rgba(34, 197, 94, 0.4);
  color: #15803d;
}

.chip-red.active {
  background: #dc2626;
  border-color: #dc2626;
  color: #fff;
}

.chip-red:not(.active):hover {
  background: rgba(239, 68, 68, 0.1);
  border-color: rgba(239, 68, 68, 0.4);
  color: #dc2626;
}

.badge {
  display: inline-block;
  padding: 2px 7px;
  border-radius: 4px;
  font-size: 0.74rem;
  font-weight: 600;
  white-space: nowrap;
}

.badge-yellow {
  background: rgba(234, 179, 8, 0.12);
  color: #b45309;
  border: 1px solid rgba(234, 179, 8, 0.25);
}

.badge-green {
  background: rgba(34, 197, 94, 0.12);
  color: #15803d;
  border: 1px solid rgba(34, 197, 94, 0.25);
}

.badge-red {
  background: rgba(239, 68, 68, 0.12);
  color: #dc2626;
  border: 1px solid rgba(239, 68, 68, 0.25);
}

.badge-gray {
  background: rgba(0, 0, 0, 0.06);
  color: #555;
  border: 1px solid rgba(0, 0, 0, 0.1);
}

.btn-sm {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 3px 9px;
  border-radius: 4px;
  font-size: 0.76rem;
  font-weight: 600;
  border: 1px solid;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s;
}

.btn-green {
  background: rgba(34, 197, 94, 0.12);
  color: #15803d;
  border-color: rgba(34, 197, 94, 0.3);
}

.btn-green:hover {
  background: rgba(34, 197, 94, 0.22);
}

.btn-red {
  background: rgba(239, 68, 68, 0.12);
  color: #dc2626;
  border-color: rgba(239, 68, 68, 0.3);
}

.btn-red:hover {
  background: rgba(239, 68, 68, 0.22);
}

.mobile-cards {
  display: none;
}

@media (max-width: 640px) {
  .table-wrapper {
    display: none;
  }

  .mobile-cards {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }
}

.mobile-card {
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 0.875rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.card-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.5rem;
}

.card-actions {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.25rem;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
  padding: 1rem;
  overflow-y: auto;
}

.modal {
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 1.5rem;
  min-width: 0;
  max-width: 520px;
  width: 100%;
  max-height: calc(100dvh - 2rem);
  overflow-y: auto;
  margin: auto;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.25rem;
}

.modal-header h2 {
  font-size: 1rem;
  font-weight: 600;
}

.form-group {
  margin-bottom: 0.85rem;
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}

.form-group label {
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--text-muted);
}

.form-group input,
.form-group select {
  background: var(--bg-input);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  color: var(--text);
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
  outline: none;
}

.filter-select {
  background: var(--bg-input);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  color: var(--text);
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
  outline: none;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 1rem;
}

.modal-section {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  font-size: 0.85rem;
  font-weight: 700;
  margin: 1.25rem 0 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid var(--border);
}

.modal-section:first-of-type {
  margin-top: 0;
  padding-top: 0;
  border-top: none;
}

.form-group-row {
  flex-direction: row;
  gap: 0.6rem;
}

.form-group-row > div {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}
</style>
